import { useState, useEffect, useRef } from 'react';
import { saveRemoteFile } from '@/utils/saveFile';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useUser } from '@/constants/UserContext';
import { supabase } from '@/lib/supabase';
import { logger } from '@/utils/logger';
import { useToast } from '@/hooks/useToast';
import { PhoneValidationResult } from '@/components/ui/PhoneInput/PhoneInput';
import { toE164Format, getCountryFromPhone } from '@/utils/phoneUtils';
import { calculateAge } from '@/utils/age';
import { compressImageFile, compressFailureMessage } from '@/utils/compressImage';
import { snapshotPickedFile } from '@/utils/pickedFile';
import { MAX_CERTIFICATE_BYTES, MAX_CERTIFICATE_LABEL } from '@/constants/uploadLimits';
import type { Certificate } from './types';
import { validatePhoneE164, uploadProfileImages } from './settingsSaveHelpers';

/** Cap on the certificate list. Truncation is logged rather than silent. */
const CERTIFICATES_LIMIT = 100;

const emptyInitial = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  phoneCountryCode: '',
  age: '',
  description: '',
  tshirtSize: '',
  skills: [] as string[],
  profileImage: '',
  backgroundImage: '',
  dateOfBirth: '',
  name: '',
  orgEmail: '',
  orgPhone: '',
  orgPhoneCountryCode: '',
  orgDescription: '',
  location: '',
  marketplaceListed: true,
};

export function useSettingsForm() {
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const { user, updateUser } = useUser();
  const { showToast, hideToast, toast } = useToast();
  const userRole = user?.role || 'volunteer';
  const isVolunteer = userRole === 'volunteer' || userRole === 'ayan';
  const isOrganization = userRole === 'organization';

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [phoneCountryCode, setPhoneCountryCode] = useState('US');
  const [age, setAge] = useState('');
  const [description, setDescription] = useState('');
  const [tshirtSize, setTshirtSize] = useState('');
  const [skills, setSkills] = useState<string[]>([]);
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [newSkill, setNewSkill] = useState('');
  const [certificateTitle, setCertificateTitle] = useState('');
  const [certificateDate, setCertificateDate] = useState('');
  const [selectedCertificateFile, setSelectedCertificateFile] = useState<File | null>(null);
  const [certificates, setCertificates] = useState<Certificate[]>([]);
  const [initialCertificates, setInitialCertificates] = useState<Certificate[]>([]);
  const [uploadingCertificate, setUploadingCertificate] = useState(false);

  const [name, setName] = useState('');
  const [orgEmail, setOrgEmail] = useState('');
  const [orgPhone, setOrgPhone] = useState('');
  const [orgPhoneCountryCode, setOrgPhoneCountryCode] = useState('US');
  const [orgDescription, setOrgDescription] = useState('');
  const [location, setLocation] = useState('');
  const [marketplaceListed, setMarketplaceListed] = useState(true);

  const [profileImage, setProfileImage] = useState('');
  const [backgroundImage, setBackgroundImage] = useState('');
  const [myProfileImage, setMyProfileImage] = useState<File | null>(null);
  const [myBackgroundImage, setMyBackgroundImage] = useState<File | null>(null);
  const [profileImagePreview, setProfileImagePreview] = useState('');
  const [backgroundImagePreview, setBackgroundImagePreview] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [processingImage, setProcessingImage] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [phoneError, setPhoneError] = useState('');
  const [orgPhoneError, setOrgPhoneError] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);
  const [isDataLoaded, setIsDataLoaded] = useState(false);

  const [initialState, setInitialState] = useState(emptyInitial);
  const profileImageInputRef = useRef<HTMLInputElement>(null);
  const backgroundImageInputRef = useRef<HTMLInputElement>(null);
  const certificateFileInputRef = useRef<HTMLInputElement>(null);
  const isInitializingRef = useRef(true);

  useEffect(() => {
    if (!user) return;
    isInitializingRef.current = true;

    if (isVolunteer) {
      let parsedPhone = '';
      let parsedPhoneCountryCode = 'US';
      if (user.phone) {
        let phoneToCheck = String(user.phone);
        if (!phoneToCheck.startsWith('+')) phoneToCheck = '+' + phoneToCheck;
        const countryCode = getCountryFromPhone(phoneToCheck);
        if (countryCode) {
          parsedPhone = toE164Format(phoneToCheck, countryCode) || phoneToCheck;
          parsedPhoneCountryCode = countryCode.toUpperCase();
        } else {
          parsedPhone = phoneToCheck.startsWith('+') ? phoneToCheck : '+' + phoneToCheck;
        }
      }
      setFirstName(user.first_name || '');
      setLastName(user.last_name || '');
      setEmail(user.email || '');
      setPhone(parsedPhone);
      setPhoneCountryCode(parsedPhoneCountryCode);
      setAge(user.age ? String(user.age) : '');
      setDescription(user.description || '');
      setTshirtSize(user.tshirtSize || '');
      setSkills(user.skills || []);
      setProfileImage(user.profile_image || '');
      setBackgroundImage(user.background_image || '');
      setDateOfBirth(user.date_of_birth || '');
      setInitialState({
        ...emptyInitial,
        firstName: user.first_name || '',
        lastName: user.last_name || '',
        email: user.email || '',
        phone: parsedPhone,
        phoneCountryCode: parsedPhoneCountryCode,
        age: user.age ? String(user.age) : '',
        description: user.description || '',
        tshirtSize: user.tshirtSize || '',
        skills: user.skills || [],
        profileImage: user.profile_image || '',
        backgroundImage: user.background_image || '',
        dateOfBirth: user.date_of_birth || '',
      });
      setDirty(false);
      setIsDataLoaded(true);
      setTimeout(() => { isInitializingRef.current = false; }, 100);
    } else if (isOrganization) {
      let parsedOrgPhone = '';
      let parsedOrgPhoneCountryCode = 'US';
      if (user.phone) {
        let orgPhoneToCheck = String(user.phone);
        if (!orgPhoneToCheck.startsWith('+')) orgPhoneToCheck = '+' + orgPhoneToCheck;
        const countryCode = getCountryFromPhone(orgPhoneToCheck);
        if (countryCode) {
          parsedOrgPhone = toE164Format(orgPhoneToCheck, countryCode) || orgPhoneToCheck;
          parsedOrgPhoneCountryCode = countryCode.toUpperCase();
        } else {
          parsedOrgPhone = orgPhoneToCheck.startsWith('+') ? orgPhoneToCheck : '+' + orgPhoneToCheck;
        }
      }
      const parsedLocation = user.location || '';
      setName(user.first_name || '');
      setOrgEmail(user.email || '');
      setOrgPhone(parsedOrgPhone);
      setOrgPhoneCountryCode(parsedOrgPhoneCountryCode);
      setOrgDescription(user.description || '');
      setProfileImage(user.profile_image || '');
      setBackgroundImage(user.background_image || '');
      setLocation(parsedLocation);
      const listed = (user as { marketplace_listed?: boolean }).marketplace_listed ?? true;
      setMarketplaceListed(listed);
      setInitialState({
        ...emptyInitial,
        name: user.first_name || '',
        orgEmail: user.email || '',
        orgPhone: parsedOrgPhone,
        orgPhoneCountryCode: parsedOrgPhoneCountryCode,
        orgDescription: user.description || '',
        location: parsedLocation,
        marketplaceListed: listed,
        profileImage: user.profile_image || '',
        backgroundImage: user.background_image || '',
      });
      setDirty(false);
      setIsDataLoaded(true);
      setTimeout(() => { isInitializingRef.current = false; }, 100);
    }
  }, [user, isVolunteer, isOrganization]);

  useEffect(() => {
    if (!user?.id || !isVolunteer) return;
    const fetchCertificates = async () => {
      const { data, error } = await supabase
        .from('certificates')
        .select('id, user_id, event_id, title, image_url, date, organization, is_public, created_at')
        .eq('user_id', user.id)
        .order('date', { ascending: false })
        .limit(CERTIFICATES_LIMIT);
      if (!error && data) {
        if (data.length === CERTIFICATES_LIMIT) {
          logger.warn(
            `useSettingsForm: user ${user.id} hit the ${CERTIFICATES_LIMIT}-certificate cap; older certificates are not listed`
          );
        }
        setCertificates(data);
        setInitialCertificates(data);
        if (!isInitializingRef.current) setDirty(false);
      }
    };
    fetchCertificates();
  }, [user?.id, isVolunteer]);

  useEffect(() => {
    if (!isDataLoaded || isInitializingRef.current) {
      setDirty(false);
      return;
    }
    if (isVolunteer) {
      const certificatesChanged = initialCertificates.length > 0 || certificates.length > 0
        ? JSON.stringify(certificates) !== JSON.stringify(initialCertificates)
        : false;
      setDirty(
        firstName !== initialState.firstName || lastName !== initialState.lastName ||
        email !== initialState.email || phone !== initialState.phone ||
        phoneCountryCode !== initialState.phoneCountryCode || age !== initialState.age ||
        description !== initialState.description || tshirtSize !== initialState.tshirtSize ||
        JSON.stringify(skills) !== JSON.stringify(initialState.skills) ||
        profileImage !== initialState.profileImage || backgroundImage !== initialState.backgroundImage ||
        dateOfBirth !== initialState.dateOfBirth || myProfileImage !== null ||
        myBackgroundImage !== null || certificatesChanged
      );
    } else if (isOrganization) {
      setDirty(
        name !== initialState.name || orgEmail !== initialState.orgEmail ||
        orgPhone !== initialState.orgPhone || orgPhoneCountryCode !== initialState.orgPhoneCountryCode ||
        orgDescription !== initialState.orgDescription || profileImage !== initialState.profileImage ||
        backgroundImage !== initialState.backgroundImage || location !== initialState.location ||
        marketplaceListed !== initialState.marketplaceListed ||
        myProfileImage !== null || myBackgroundImage !== null
      );
    }
  }, [
    isDataLoaded, firstName, lastName, email, phone, phoneCountryCode, age, description, tshirtSize, skills,
    name, orgEmail, orgPhone, orgPhoneCountryCode, orgDescription, location, marketplaceListed,
    profileImage, backgroundImage, dateOfBirth, myProfileImage, myBackgroundImage,
    certificates, initialState, initialCertificates, isVolunteer, isOrganization
  ]);

  const uploadImageToStorage = async (file: File, bucket: string, path: string): Promise<string> => {
    const { error } = await supabase.storage.from(bucket).upload(path, file, { contentType: file.type, upsert: true });
    if (error) throw error;
    const { data: publicUrlData } = supabase.storage.from(bucket).getPublicUrl(path);
    return publicUrlData.publicUrl;
  };

  const handleProfileImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setProcessingImage(true);
    try {
      const result = await compressImageFile(file);
      if (!result.ok) {
        showToast(compressFailureMessage(result.reason), 'error');
        return;
      }
      setMyProfileImage(result.file);
      const reader = new FileReader();
      reader.onloadend = () => setProfileImagePreview(reader.result as string);
      reader.readAsDataURL(result.file);
      setDirty(true);
    } finally {
      setProcessingImage(false);
    }
  };

  const handleBackgroundImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setProcessingImage(true);
    try {
      const result = await compressImageFile(file);
      if (!result.ok) {
        showToast(compressFailureMessage(result.reason), 'error');
        return;
      }
      setMyBackgroundImage(result.file);
      const reader = new FileReader();
      reader.onloadend = () => setBackgroundImagePreview(reader.result as string);
      reader.readAsDataURL(result.file);
      setDirty(true);
    } finally {
      setProcessingImage(false);
    }
  };

  const handleRemoveBackground = () => {
    setBackgroundImage('');
    setBackgroundImagePreview('');
    setMyBackgroundImage(null);
    if (backgroundImageInputRef.current) backgroundImageInputRef.current.value = '';
    setDirty(true);
  };

  const handlePhoneValidation = (validation: PhoneValidationResult) => {
    setPhoneError(validation.isValid ? '' : (validation.error || 'Invalid phone number'));
  };
  const handleOrgPhoneValidation = (validation: PhoneValidationResult) => {
    setOrgPhoneError(validation.isValid ? '' : (validation.error || 'Invalid phone number'));
  };

  const handleAddSkill = () => {
    if (newSkill.trim() && !skills.includes(newSkill.trim())) {
      setSkills((prev) => [...prev, newSkill.trim()]);
      setNewSkill('');
    }
  };
  const handleDeleteSkill = (skillToDelete: string) => {
    setSkills((prev) => prev.filter((s) => s !== skillToDelete));
  };

  const handleCertificateFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const isPdfMime = file.type === 'application/pdf';
      const isPdfExtension = file.name.toLowerCase().endsWith('.pdf');
      if (!isPdfMime && !isPdfExtension) {
        showToast('Only PDF files are allowed', 'error');
        setSelectedCertificateFile(null);
        return;
      }
      if (file.size > MAX_CERTIFICATE_BYTES) {
        showToast(`Certificate file size must be less than ${MAX_CERTIFICATE_LABEL}`, 'error');
        setSelectedCertificateFile(null);
        return;
      }
      // Read it now: the file waits in state until upload, by which point
      // Android may have revoked the picker's read permission.
      const snapshot = await snapshotPickedFile(file);
      if (!snapshot) {
        showToast('Could not read that file. Please pick it again.', 'error');
        setSelectedCertificateFile(null);
        return;
      }
      setSelectedCertificateFile(snapshot);
    }
  };

  const handleUploadCertificate = async () => {
    if (!certificateTitle.trim()) { showToast('Please enter a certificate title', 'error'); return; }
    if (!certificateDate) { showToast('Please select a certificate date', 'error'); return; }
    const file = selectedCertificateFile;
    if (!file) { showToast('Please select a PDF file', 'error'); return; }
    try {
      setUploadingCertificate(true);
      const publicUrl = await uploadImageToStorage(file, 'volunteer-certificates', `${user?.id}/certificate_${Date.now()}.pdf`);
      const isoDate = new Date(certificateDate).toISOString().split('T')[0];
      const { data: newCertificate, error: dbError } = await supabase
        .from('certificates')
        .insert({ user_id: user?.id, title: certificateTitle.trim(), organization: 'Self-uploaded', date: isoDate, image_url: publicUrl, is_public: true })
        .select()
        .single();
      if (dbError) throw dbError;
      setCertificates(prev => [newCertificate, ...prev]);
      setCertificateTitle('');
      setCertificateDate('');
      setSelectedCertificateFile(null);
      if (certificateFileInputRef.current) certificateFileInputRef.current.value = '';
      showToast('Certificate uploaded successfully!', 'success');
    } catch (err: unknown) {
      const message =
        typeof err === 'object' && err !== null && 'message' in err && typeof (err as { message?: unknown }).message === 'string'
          ? (err as { message: string }).message
          : err instanceof Error
            ? err.message
            : String(err);
      showToast(`Failed to upload certificate: ${message}`, 'error');
    } finally {
      setUploadingCertificate(false);
    }
  };

  const handleDeleteCertificate = async (certificateId: string, imageUrl: string) => {
    if (!confirm('Are you sure you want to delete this certificate? This action cannot be undone.')) return;
    try {
      const { error: dbError } = await supabase.from('certificates').delete().eq('id', certificateId);
      if (dbError) throw dbError;
      const urlParts = imageUrl.split('/volunteer-certificates/');
      if (urlParts.length > 1) await supabase.storage.from('volunteer-certificates').remove([urlParts[1]]);
      setCertificates(prev => prev.filter(c => c.id !== certificateId));
      setInitialCertificates(prev => prev.filter(c => c.id !== certificateId));
      showToast('Certificate deleted successfully', 'success');
    } catch (err: unknown) {
      const message =
        typeof err === 'object' && err !== null && 'message' in err && typeof (err as { message?: unknown }).message === 'string'
          ? (err as { message: string }).message
          : err instanceof Error
            ? err.message
            : String(err);
      showToast(`Failed to delete certificate: ${message}`, 'error');
    }
  };

  const handleDownloadCertificate = async (certificate: Certificate) => {
    try {
      await saveRemoteFile(certificate.image_url, { title: certificate.title });
    } catch {
      showToast('Failed to download certificate', 'error');
    }
  };

  const toggleCertificatePrivacy = (certificateId: string, currentPrivacy: boolean) => {
    setCertificates(prev => prev.map(c => c.id === certificateId ? { ...c, is_public: !currentPrivacy } : c));
    setDirty(true);
  };

  const handleSaveChanges = async () => {
    setIsSaving(true);
    try {
      if (isVolunteer) {
        const e164 = phone || '';
        if (!validatePhoneE164(e164, setPhoneError)) {
          setIsSaving(false);
          return;
        }
        const { profileImage: finalProfileImage, backgroundImage: finalBackgroundImage } = await uploadProfileImages(
          'volunteer-avatars',
          `volunteers/${user!.id}`,
          myProfileImage,
          myBackgroundImage,
          profileImage,
          backgroundImage,
          !backgroundImage && !backgroundImagePreview && myBackgroundImage === null
        );
        const calculatedAge = dateOfBirth ? calculateAge(dateOfBirth) : null;
        const parsedAge = age ? parseInt(age, 10) : NaN;
        // Never send "" to nullable columns. An empty phone hits a numeric
        // cast in the DB and fails with "invalid input syntax for type
        // numeric: """; send null instead (null casts cleanly). Same for the
        // other optional fields.
        const finalAge =
          calculatedAge !== null && Number.isFinite(calculatedAge)
            ? calculatedAge
            : Number.isFinite(parsedAge)
              ? parsedAge
              : null;
        const updates = {
          first_name: firstName,
          last_name: lastName,
          email,
          phone: e164 || null,
          age: finalAge,
          date_of_birth: dateOfBirth || null,
          description: description.trim() || null,
          tshirtSize: tshirtSize || null,
          skills,
          profile_image: finalProfileImage || null,
          background_image: finalBackgroundImage || null,
        };
        const { error: profileError } = await supabase.from('profiles').update(updates).eq('id', user?.id);
        if (profileError) throw profileError;
        for (const cert of certificates) {
          const initialCert = initialCertificates.find((c) => c.id === cert.id);
          const initialPrivacy = initialCert?.is_public ?? true;
          if (cert.is_public !== initialPrivacy) {
            await supabase
              .from('certificates')
              .update({ is_public: cert.is_public ?? true })
              .eq('id', cert.id);
          }
        }
        // The DB gets null to clear columns; the local context type uses
        // undefined, so map nulls back to undefined for updateUser.
        updateUser({
          ...updates,
          phone: updates.phone ?? undefined,
          age: updates.age ?? undefined,
          date_of_birth: updates.date_of_birth ?? undefined,
          description: updates.description ?? undefined,
          tshirtSize: updates.tshirtSize ?? undefined,
          profile_image: updates.profile_image ?? undefined,
          background_image: updates.background_image ?? undefined,
        });
        setProfileImage(finalProfileImage);
        setBackgroundImage(finalBackgroundImage);
        setMyProfileImage(null);
        setMyBackgroundImage(null);
        setProfileImagePreview('');
        setBackgroundImagePreview('');
        setInitialCertificates([...certificates]);
        setInitialState((prev) => ({ ...prev, firstName, lastName, email, phone, phoneCountryCode, age, description, tshirtSize, skills, profileImage: finalProfileImage, backgroundImage: finalBackgroundImage, dateOfBirth }));
        setDirty(false);
        showToast('Profile updated successfully!', 'success');
      } else if (isOrganization) {
        const orgE164 = orgPhone || '';
        if (!validatePhoneE164(orgE164, setOrgPhoneError)) {
          setIsSaving(false);
          return;
        }
        const { profileImage: finalOrgProfileImage, backgroundImage: finalOrgBackgroundImage } = await uploadProfileImages(
          'volunteer-avatars',
          `organizations/${user!.id}`,
          myProfileImage,
          myBackgroundImage,
          profileImage,
          backgroundImage,
          !backgroundImage && !backgroundImagePreview && myBackgroundImage === null
        );
        const updates = {
          first_name: name,
          email: orgEmail,
          phone: orgE164 || null,
          description: orgDescription.trim() || null,
          profile_image: finalOrgProfileImage || null,
          background_image: finalOrgBackgroundImage || null,
          location: location || null,
          marketplace_listed: marketplaceListed,
        };
        const { error: profileError } = await supabase.from('profiles').update(updates).eq('id', user?.id);
        if (profileError) throw profileError;
        updateUser({
          ...updates,
          phone: updates.phone ?? undefined,
          description: updates.description ?? undefined,
          profile_image: updates.profile_image ?? undefined,
          background_image: updates.background_image ?? undefined,
          location: updates.location ?? undefined,
        });
        setProfileImage(finalOrgProfileImage);
        setBackgroundImage(finalOrgBackgroundImage);
        setMyProfileImage(null);
        setMyBackgroundImage(null);
        setProfileImagePreview('');
        setBackgroundImagePreview('');
        setInitialState((prev) => ({
          ...prev,
          name,
          orgEmail,
          orgPhone,
          orgPhoneCountryCode,
          orgDescription,
          profileImage: finalOrgProfileImage,
          backgroundImage: finalOrgBackgroundImage,
          location,
          marketplaceListed,
        }));
        setDirty(false);
        showToast('Organization profile updated successfully!', 'success');
      }
    } catch (err: unknown) {
      const message =
        err instanceof Error
          ? err.message
          : typeof err === 'object' && err !== null && 'message' in err
            ? String((err as { message: unknown }).message)
            : 'Error';
      showToast(`Failed to save changes: ${message}`, 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    if (isVolunteer) {
      setFirstName(initialState.firstName);
      setLastName(initialState.lastName);
      setEmail(initialState.email);
      setPhone(initialState.phone);
      setPhoneCountryCode(initialState.phoneCountryCode);
      setAge(initialState.age);
      setDescription(initialState.description);
      setTshirtSize(initialState.tshirtSize);
      setSkills(initialState.skills);
      setProfileImage(initialState.profileImage);
      setBackgroundImage(initialState.backgroundImage);
      setDateOfBirth(initialState.dateOfBirth);
      setCertificates([...initialCertificates]);
    } else if (isOrganization) {
      setName(initialState.name);
      setOrgEmail(initialState.orgEmail);
      setOrgPhone(initialState.orgPhone);
      setOrgPhoneCountryCode(initialState.orgPhoneCountryCode);
      setOrgDescription(initialState.orgDescription);
      setProfileImage(initialState.profileImage);
      setBackgroundImage(initialState.backgroundImage);
      setLocation(initialState.location);
      setMarketplaceListed(initialState.marketplaceListed);
    }
    setMyProfileImage(null);
    setMyBackgroundImage(null);
    setProfileImagePreview('');
    setBackgroundImagePreview('');
    // Reset file inputs to allow selecting the same file again
    if (profileImageInputRef.current) profileImageInputRef.current.value = '';
    if (backgroundImageInputRef.current) backgroundImageInputRef.current.value = '';
    setDirty(false);
  };

  const handleDeleteAccount = async () => {
    if (!user?.id) return;
    setIsDeletingAccount(true);
    try {
      try {
        const { data, error } = await supabase.functions.invoke('delete-user-account', { body: { userId: user.id } });
        if (error) throw error;
        if (!data?.success) throw new Error('Edge function returned failure');
        await signOut();
        showToast('Account deleted successfully', 'success');
        setTimeout(() => navigate('/'), 2000);
        return;
      } catch {
        await supabase.from('profiles').delete().eq('id', user.id);
        if (user.role === 'organization') await supabase.from('events').delete().eq('created_by', user.id);
        await supabase.from('volunteer_applications').delete().eq('volunteer_id', user.id);
        if (user.role === 'volunteer') await supabase.from('volunteers').delete().eq('volunteer_id', user.id);
        await supabase.from('messages').delete().eq('sender_id', user.id);
        await supabase.from('message_reads').delete().eq('user_id', user.id);
        await supabase.from('messages_new').delete().eq('user_id', user.id);
        await supabase.from('notifications').delete().eq('user_id', user.id);
        await supabase.storage.from('volunteer-avatars').remove([`volunteers/${user.id}`]);
        if (user.role === 'organization') await supabase.storage.from('volunteer-avatars').remove([`organizations/${user.id}`]);
        {
          const bucket = 'volunteer-certificates';
          const folder = user.id;
          const { data: certFiles, error: listCertError } = await supabase.storage.from(bucket).list(folder);
          if (!listCertError && certFiles?.length) {
            const paths = certFiles.map((f) => `${folder}/${f.name}`);
            await supabase.storage.from(bucket).remove(paths);
          }
        }
        await signOut();
        showToast('Account deleted successfully', 'success');
        setTimeout(() => navigate('/'), 2000);
      }
    } catch (err: unknown) {
      const message =
        typeof err === 'object' && err !== null && 'message' in err && typeof (err as { message?: unknown }).message === 'string'
          ? (err as { message: string }).message
          : err instanceof Error
            ? err.message
            : String(err);
      showToast(`Failed to delete account: ${message}`, 'error');
    } finally {
      setIsDeletingAccount(false);
      setShowDeleteModal(false);
    }
  };

  return {
    user,
    userRole,
    isVolunteer,
    isOrganization,
    isDataLoaded,
    dirty,
    isSaving,
    showDeleteModal,
    setShowDeleteModal,
    isDeletingAccount,
    profileImageInputRef,
    backgroundImageInputRef,
    certificateFileInputRef,
    firstName, setFirstName,
    lastName, setLastName,
    email,
    phone, setPhone,
    phoneCountryCode, setPhoneCountryCode,
    phoneError,
    description, setDescription,
    dateOfBirth, setDateOfBirth,
    tshirtSize, setTshirtSize,
    calculateAge,
    name, setName,
    orgEmail,
    orgPhone, setOrgPhone,
    orgPhoneCountryCode, setOrgPhoneCountryCode,
    orgPhoneError,
    orgDescription, setOrgDescription,
    location, setLocation,
    marketplaceListed, setMarketplaceListed,
    profileImage,
    profileImagePreview,
    backgroundImage,
    backgroundImagePreview,
    newSkill, setNewSkill,
    skills,
    certificateTitle, setCertificateTitle,
    certificateDate, setCertificateDate,
    selectedCertificateFile,
    certificates,
    uploadingCertificate,
    handleProfileImageChange,
    handleBackgroundImageChange,
    processingImage,
    handleRemoveBackground,
    handlePhoneValidation,
    handleOrgPhoneValidation,
    handleAddSkill,
    handleDeleteSkill,
    handleCertificateFileChange,
    handleUploadCertificate,
    handleDeleteCertificate,
    handleDownloadCertificate,
    toggleCertificatePrivacy,
    handleSaveChanges,
    handleCancel,
    handleDeleteAccount,
    toast,
    hideToast,
  };
}

export type SettingsFormReturn = ReturnType<typeof useSettingsForm>;
